import { useEffect, useMemo, useRef, useState } from 'react';
import productImageService, { ProductImage, ProductImageImport, ProductImageImportInput } from '../services/api/productImageService';
import tokenManager from '../utils/tokenManager';

interface ImageSaveState {
  status: 'saving' | 'saved' | 'failed';
  savedImage?: ProductImage;
  error?: string;
}

interface ImportReceipt {
  requestId: string;
  importId?: string;
  images: ProductImageImportInput[];
}

interface View {
  images: ProductImageImportInput[];
  state: ProductImageImport | null;
  busy: boolean;
  error: string | null;
}

const EMPTY: View = { images: [], state: null, busy: false, error: null };
const POLL_MS = 3000;

function requestUuid() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** A receipt survives reload/close so an unknown POST result never creates a new batch. */
export default function useProductImageImport({
  isOpen, productId, url, onComplete,
}: {
  isOpen: boolean;
  productId?: number;
  url: string;
  onComplete: (images: ProductImage[]) => void;
}) {
  const [view, setView] = useState<View>(EMPTY);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const actions = useRef<{
    save: (images: ProductImageImportInput[]) => Promise<void>;
    retry: (url: string) => Promise<void>;
    resume: () => Promise<void>;
    complete: () => void;
  } | null>(null);
  const userId = tokenManager.getUserData()?.id ?? 'current';
  const storageKey = `product-image-import:v1:${userId}:${productId}:${url}`;

  useEffect(() => {
    setView(EMPTY);
    if (!isOpen || !productId || !url) {
      actions.current = null;
      return;
    }

    let active = true;
    let receipt: ImportReceipt | null = null;
    let current: ProductImageImport | null = null;
    let inFlight: AbortController | null = null;
    let operation: 'start' | 'poll' | 'retry' | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pollStopped = false;
    let recoverableError = false;
    let completed = false;

    const clearTimer = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };
    const persist = () => {
      sessionStorage.setItem(storageKey, JSON.stringify(receipt));
    };
    const complete = () => {
      if (!active || completed || !current || inFlight || pollStopped || current.pending > 0) return;
      const images = current.items.flatMap((item) => item.status === 'completed' && item.image ? [item.image] : []);
      if (!images.length) return;
      completed = true;
      clearTimer();
      try { sessionStorage.removeItem(storageKey); } catch { /* Receipt remains safe to resume. */ }
      onCompleteRef.current(images);
    };
    const schedule = () => {
      clearTimer();
      if (active && !completed && !pollStopped && current && current.pending > 0 && !document.hidden) {
        timer = setTimeout(() => { void run('poll'); }, POLL_MS);
      }
    };

    const run = async (kind: 'start' | 'poll' | 'retry', itemIds: number[] = []) => {
      if (!active || completed || !receipt || inFlight) return;
      if (kind === 'poll' && document.hidden) return;
      clearTimer();
      pollStopped = false;
      recoverableError = false;
      const controller = new AbortController();
      inFlight = controller;
      operation = kind;
      setView((prev) => ({ ...prev, images: receipt!.images, busy: true, error: null }));
      try {
        const response = kind === 'start'
          ? await productImageService.startImport(productId, receipt.requestId, receipt.images, controller.signal)
          : kind === 'retry'
            ? await productImageService.retryImport(productId, receipt.importId!, itemIds, controller.signal)
            : await productImageService.getImport(productId, receipt.importId!, controller.signal);
        if (!active || controller.signal.aborted) return;
        current = response.data;
        receipt.importId = current.id;
        // The original request/payload was persisted before enqueue; if this update fails,
        // reissuing that exact request after reload still resolves to the existing import.
        try { persist(); } catch { /* Preserve the already persisted request UUID. */ }
        setView({ images: receipt.images, state: current, busy: false, error: null });
      } catch (error: any) {
        if (!active || controller.signal.aborted) return;
        pollStopped = true;
        const status = error?.response?.status;
        recoverableError = status == null || status === 408 || status === 429 || (status >= 500 && status < 600);
        if (kind === 'start' && status === 422) {
          // Validation did not enqueue anything; the user may correct their selection.
          try { sessionStorage.removeItem(storageKey); } catch { /* Keep in-memory state usable. */ }
          receipt = null;
          setView({ ...EMPTY, error: error?.response?.data?.message || '画像の選択内容を確認してください。' });
        } else {
          const message = status === 401 || status === 403 || status === 404
            ? '保存状況を確認できません。ログイン状態や商品の閲覧権限を確認してください。'
            : kind === 'start'
              ? '受付状況を確認できませんでした。再試行すると同じ受付内容を確認します。'
              : '保存状況を取得できませんでした。再試行して状況を確認してください。';
          setView((prev) => ({ ...prev, busy: false, error: message }));
        }
      } finally {
        if (inFlight === controller) {
          inFlight = null;
          operation = null;
        }
        if (active && !controller.signal.aborted && !pollStopped && current?.pending === 0 && current.failed === 0) {
          complete();
        }
        if (active) schedule();
      }
    };

    const resume = async () => {
      if (!receipt) return;
      await run(receipt.importId ? 'poll' : 'start');
    };
    actions.current = {
      save: async (images) => {
        if (inFlight || completed) return;
        if (receipt) {
          if (current?.pending === 0 && current.failed > 0 && !pollStopped) {
            await run('retry', current.items.filter((item) => item.status === 'failed').map((item) => item.id));
          } else {
            await resume();
          }
          return;
        }
        if (!images.length) return;
        receipt = { requestId: requestUuid(), images: images.map(({ url: imageUrl, category, title, description }) => ({
          url: imageUrl, category, title, description,
        })) };
        try { persist(); } catch {
          receipt = null;
          setView({ ...EMPTY, error: '受付情報を保存できませんでした。ブラウザの設定を確認して、もう一度お試しください。' });
          return;
        }
        await run('start');
      },
      retry: async (imageUrl) => {
        if (pollStopped) { await resume(); return; }
        const item = current?.items.find((entry) => entry.url === imageUrl && entry.status === 'failed');
        if (item) await run('retry', [item.id]);
      },
      resume,
      complete,
    };

    const visibilityChanged = () => {
      clearTimer();
      if (document.hidden) {
        if (operation === 'poll') inFlight?.abort();
      } else if ((!pollStopped || recoverableError) && !inFlight && receipt && (!current || current.pending > 0 || recoverableError)) {
        void resume();
      }
    };
    // Each recovery event makes one attempt. A failed attempt stays paused until
    // another event or an explicit retry; authorization failures never auto-retry.
    const online = () => {
      if (pollStopped && recoverableError && !document.hidden) void resume();
    };
    document.addEventListener('visibilitychange', visibilityChanged);
    window.addEventListener('online', online);
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ImportReceipt;
        if (typeof parsed.requestId === 'string' && Array.isArray(parsed.images) && parsed.images.length > 0) {
          receipt = parsed;
          setView({ ...EMPTY, images: parsed.images, busy: true });
          void resume();
        }
      }
    } catch { /* A new enqueue will verify storage is writable before sending. */ }

    return () => {
      active = false;
      clearTimer();
      inFlight?.abort();
      actions.current = null;
      document.removeEventListener('visibilitychange', visibilityChanged);
      window.removeEventListener('online', online);
    };
  }, [isOpen, productId, url, storageKey]);

  const derived = useMemo(() => {
    const imageSaveStatus = new Map<string, ImageSaveState>();
    view.images.forEach((image) => imageSaveStatus.set(image.url, { status: 'saving' }));
    const savedImages: ProductImage[] = [];
    const uploadErrors: { index: number; url: string; error: string }[] = [];
    view.state?.items.forEach((item, index) => {
      if (item.status === 'completed') {
        imageSaveStatus.set(item.url, { status: 'saved', savedImage: item.image ?? undefined });
        if (item.image) savedImages.push(item.image);
      } else if (item.status === 'failed') {
        const error = item.error || '保存に失敗しました';
        imageSaveStatus.set(item.url, { status: 'failed', error });
        uploadErrors.push({ index, url: item.url, error });
      } else {
        imageSaveStatus.set(item.url, { status: 'saving' });
      }
    });
    return {
      imageSaveStatus, savedImages, uploadErrors,
      saveProgress: view.images.length ? {
        saved: view.state?.completed ?? 0,
        total: view.images.length,
        processing: view.state?.pending ?? view.images.length,
      } : null,
    };
  }, [view.images, view.state]);

  return {
    ...derived,
    saving: view.busy || (!view.error && (view.state?.pending ?? 0) > 0),
    error: view.error,
    importImages: view.images,
    save: (images: ProductImageImportInput[]) => actions.current?.save(images),
    retry: (imageUrl: string) => actions.current?.retry(imageUrl),
    resume: () => actions.current?.resume(),
    complete: () => actions.current?.complete(),
  };
}
